import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import {
  NotificationsList,
  ResearchNotifications,
} from 'notifications-from-civil-status/view-models/notifications-list';
import { getLabelAmbito, IdAmbito } from 'shell/constants/id-ambito';
import {
  getLabelStateNotification,
  IdStateNotification,
} from 'notifications-from-civil-status/constants/id-state-notification';
import { Edit, Visibility } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import DeleteDialog from 'shell/components/shared/delete-dialog';

interface TableActProps {
  data?: Array<NotificationsList>;
  filterSelected?: ResearchNotifications;
  getNotificationsListData: (
    data: ResearchNotifications,
    page?: number,
    rowsPerPage?: number
  ) => void;
  tableProps?: Record<string, number>;
  baseUrl?: string;
  addedActions?: (item: NotificationsList) => ReactElement;
  checkVisibilityActions?: { read?: boolean; edit?: boolean; delete?: boolean };
}

const TableNotificationsComponent = ({
  data,
  filterSelected,
  getNotificationsListData,
  tableProps = {},
  baseUrl,
  addedActions = () => <></>,
  checkVisibilityActions = { read: false, edit: false, delete: false },
}: TableActProps): ReactElement => {
  const [dataList, setDataList] = useState<Array<NotificationsList> | undefined>();
  const [elementToDelete, setElementToDelete] = useState<NotificationsList | undefined>(undefined);

  const navigate = useNavigate();

  useEffect(() => {
    setDataList(data);
  }, [data]);

  const deleteData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onTrashPress = useEventCallback(() => {
    if (!elementToDelete?.idNotifica) {
      return;
    }

    (
      deleteData({
        data: {
          key: {
            idAggiornamentoAnagrafe: elementToDelete?.idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.Annullata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      setElementToDelete(undefined);
      toast.success('Operazione eseguita con successo');
      getNotificationsListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });
  });

  const clearToDelete = useEventCallback(() => {
    setElementToDelete(undefined);
  });

  const onDetailPress = useEventCallback((item: NotificationsList) => {
    if (!item.idAnsc || !baseUrl || !item.idNotifica) {
      return;
    }

    navigate({
      pathname: generatePath(`${baseUrl}/visualizza/:id/:idNotifica`, {
        id: item.idAnsc,
        idNotifica: item.idNotifica?.toString() || '',
      }),
    });
  });

  const onEditPress = useEventCallback((item: NotificationsList) => {
    if (!item.idAnsc || !baseUrl || !item.idNotifica) {
      return;
    }

    navigate({
      pathname: generatePath(`${baseUrl}/modifica/:id/:idNotifica`, {
        id: item.idAnsc,
        idNotifica: item.idNotifica?.toString() || '',
      }),
    });
  });

  const body: Array<NotificationsList> = useMemo(() => {
    return (dataList || []).map((item) => {
      return {
        ...item,
        scope: getLabelAmbito(item.scope?.toString() || ''),
        state: getLabelStateNotification(item.state?.toString() || ''),
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDetailPress(item);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
              disabled={
                !(
                  item.state === IdStateNotification.InElaborazione ||
                  item.state === IdStateNotification.RegistrazioneEffettuata ||
                  item.state === IdStateNotification.Annullata
                )
              }
            >
              <Visibility />
            </IconButton>
            {(checkVisibilityActions.edit &&
              (item.scope === IdAmbito.Iscrizioni ||
                item.scope === IdAmbito.Mutazioni ||
                item.scope === IdAmbito.Cancellazioni)) ||
            !checkVisibilityActions.edit ? (
              <IconButton
                aria-label="modifica"
                component="span"
                onClick={() => onEditPress(item)}
                disabled={
                  item.state === IdStateNotification.RegistrazioneEffettuata ||
                  item.state === IdStateNotification.Annullata
                }
              >
                <Edit />
              </IconButton>
            ) : null}
            {addedActions(item)}
            <IconButton
              color="error"
              aria-label="elimina"
              component="span"
              onClick={() => setElementToDelete(item)}
              disabled={
                item.state !== IdStateNotification.DaRegistrare &&
                item.state !== IdStateNotification.InElaborazione
              }
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ),
      };
    });
  }, [dataList, addedActions, checkVisibilityActions]);

  const onChangePage = useEventCallback((page: number) => {
    getNotificationsListData(filterSelected || {}, page, tableProps.rowsPerPage);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getNotificationsListData(filterSelected || {}, 0, rowsPerPage);
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista notifiche"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
      {Boolean(elementToDelete) ? (
        <DeleteDialog
          isOpen={Boolean(elementToDelete)}
          onAbort={clearToDelete}
          onClose={clearToDelete}
          onConfirm={onTrashPress}
          text="Sei sicuro di voler eliminare la richiesta di aggiornamento? Non sarà più possibile effettuarla in automatico."
        />
      ) : null}
    </Grid>
  );
};

TableNotificationsComponent.displayName = 'Table Notifications component';

const TableNotifications = memo(TableNotificationsComponent);

export default TableNotifications;
